import { DragEvent, useEffect, useRef, useState } from "react";
import { AuditStep, Policy } from ".";
import { AlertCircle, GripVertical, X } from "lucide-react";

function StepCard({
  step,
  index,
  stepNumber,
  onRemovePolicy,
  onMove,
  onToggleResponseCheck,
  onTogglePreSendReview,
  onAddPolicyToStep,
  hoverStepIndex,
  setHoverStepIndex,
  onHeightChange,
  renderIconByName,
}: {
  step: AuditStep;
  index: number;
  stepNumber: number;
  renderIconByName: (iconName: string) => React.ReactNode;

  onRemovePolicy: (stepIndex: number, policyId: string) => void;
  onMove: (fromIndex: number, toIndex: number) => void;
  onToggleResponseCheck: (index: number) => void;
  onTogglePreSendReview: (index: number) => void;
  onAddPolicyToStep: (stepIndex: number, policy: Policy) => void;

  hoverStepIndex: number | null;
  setHoverStepIndex: (idx: number | null) => void;
  onHeightChange: (index: number, height: number) => void;
}) {
  const cardRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (cardRef.current) {
      const stepContainer = cardRef.current.querySelector('.step-container') as HTMLElement;
      if (stepContainer) {
        onHeightChange(index, stepContainer.offsetHeight);
      }
    }
  }, [step, hoverStepIndex, index, onHeightChange]);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);


  const handleDragStart = (e: DragEvent<HTMLDivElement>) => {
    setIsDragging(true);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", `step-${index}`);
    e.dataTransfer.setData(
      "application/json",
      JSON.stringify({ type: "STEP", index }),
    );
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;

    if (
      e.dataTransfer.types.includes("application/json") ||
      e.dataTransfer.types.includes("text/plain")
    ) {
      setHoverStepIndex(index);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;

    if (dragCounter.current === 0) {
      setHoverStepIndex(null);
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setHoverStepIndex(null);
    dragCounter.current = 0;

    try {
      const jsonData = e.dataTransfer.getData("application/json");
      if (jsonData) {
        const data = JSON.parse(jsonData);

        if (data.type === "POLICY") {
          onAddPolicyToStep(index, data.policy);
        } else if (data.type === "STEP") {
          const fromIndex = data.index;
          if (fromIndex !== index) {
            onMove(fromIndex, index);
          }
        }
      }
    } catch (err) {
      console.error("Drop error:", err);
    }
  };

  return (
    <div
      ref={cardRef}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`step-card ${isDragging ? "dragging" : ""}`}
    >
      {hoverStepIndex === index && (
        <div className="step-hover-overlay">
          <div className="overlay-content">
            <div className="overlay-plus">+</div>
            <div className="overlay-label">Add another data collection stage</div>
          </div>
        </div>
      )}
      <div className="step-card-inner">

        {/* Step Header */}
        <div className="sequence-step-header">
          <div className="step-drag-handle">
            <GripVertical />
          </div>
          <div className="step-number-badge">Stage {stepNumber}</div>
        </div>

        {/* Step Container */}
        <div className="step-container">
          {/* Policies */}
          <div className="step-policies">
            {step.policies.map((policy) => (
              <div key={policy.id} className="step-policy-card">
                <div className={`step-policy-icon ${policy.iconColor}`}>
                  {renderIconByName(policy.icon)}
                </div>
                <div className="step-policy-content">
                  <h3 className="step-policy-name">{policy.name}</h3>
                </div>
                <button
                  className="step-policy-remove"
                  onClick={() => onRemovePolicy(index, policy.id)}
                  aria-label="Remove policy"
                >
                  <X />
                </button>
              </div>
            ))}
          </div>

          {/* Checkpoints */}
          <div className="step-checkpoints">
            <div className="checkpoints-header">
              <AlertCircle />
              <h4>Auditor Review Gates</h4>
            </div>

            {/* For first step (index === 0): Pre-Send Review comes first */}
            {index === 0 ? (
              <>
                {/* Pre-Send Review */}
                <div
                  className={`checkpoint-item ${step.preSendReview || index === 0 ? "active" : ""} ${index === 0 ? "disabled" : ""}`}
                  onClick={() => index !== 0 && onTogglePreSendReview(index)}
                  style={
                    index === 0 ? { cursor: "not-allowed", opacity: 0.7 } : {}
                  }
                >
                  <div
                    className={`checkpoint-checkbox ${step.preSendReview || index === 0 ? "checked" : ""}`}
                  />
                  <div className="checkpoint-content">
                    <p className="checkpoint-label">
                      Questions{index === 0 ? " (Required)" : ""}
                    </p>
                    <p className="checkpoint-desc">
                      Review AI generated questions
                    </p>
                  </div>
                </div>

                {/* Response Check */}
                <div
                  className={`checkpoint-item ${step.responseCheck ? "active" : ""}`}
                  onClick={() => onToggleResponseCheck(index)}
                >
                  <div
                    className={`checkpoint-checkbox ${step.responseCheck ? "checked" : ""}`}
                  />
                  <div className="checkpoint-content">
                    <p className="checkpoint-label">Response</p>
                    <p className="checkpoint-desc">
                      Review responses before next stage
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Pre-Send Review */}
                <div
                  className={`checkpoint-item ${step.preSendReview || index === 0 ? "active" : ""} ${index === 0 ? "disabled" : ""}`}
                  onClick={() => index !== 0 && onTogglePreSendReview(index)}
                  style={
                    index === 0 ? { cursor: "not-allowed", opacity: 0.7 } : {}
                  }
                >
                  <div
                    className={`checkpoint-checkbox ${step.preSendReview || index === 0 ? "checked" : ""}`}
                  />
                  <div className="checkpoint-content">
                    <p className="checkpoint-label">
                      Questions{index === 0 ? " (Required)" : ""}
                    </p>
                    <p className="checkpoint-desc">
                      Review AI generated questions
                    </p>
                  </div>
                </div>
                {/* Response Check */}
                <div
                  className={`checkpoint-item ${step.responseCheck ? "active" : ""}`}
                  onClick={() => onToggleResponseCheck(index)}
                >
                  <div
                    className={`checkpoint-checkbox ${step.responseCheck ? "checked" : ""}`}
                  />
                  <div className="checkpoint-content">
                    <p className="checkpoint-label">Response</p>
                    <p className="checkpoint-desc">
                      Review responses before next stage
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
export default StepCard;
